/**
 * @typedef {object} MeasurementInput
 * @property {number} n Total segment count.
 * @property {number} hostContentW Available width inside the host, in pixels.
 * @property {number[]} widths Intrinsic widths of each segment tag (index-aligned with segments).
 * @property {number} gapX Horizontal gap between adjacent tags.
 * @property {number} plusWidth Rendered width of the `+N` overflow button.
 * @property {boolean} expanded True when the user has expanded the full list.
 * @property {number} safetyPx Safety margin subtracted from the host width when reserving space for `+N`.
 */

/**
 * @typedef {object} MeasurementResult
 * @property {number} visible Number of tags that should render normally.
 * @property {number} hidden Number of tags collapsed into the `+N` overflow.
 * @property {boolean} scroll True when a single tag overflows and should render in a scroller.
 */

/**
 * Decide the visible/hidden counts (and the single-scroll flag) given
 * measurements. Split out from the component so it can be unit-tested without
 * driving Honeycomb primitives or jsdom's zero-width geometry.
 *
 * @param {MeasurementInput} input
 * @returns {MeasurementResult}
 */
export const computeVisibleCount = ({
	n,
	hostContentW,
	widths,
	gapX,
	plusWidth,
	expanded,
	safetyPx,
}) => {
	if (n === 0) return { visible: 0, hidden: 0, scroll: false };
	if (expanded) return { visible: n, hidden: 0, scroll: false };

	if (n === 1) {
		const tagW = widths[0] ?? 0;
		return { visible: 1, hidden: 0, scroll: tagW > hostContentW + 0.5 };
	}

	const prefix = new Array(widths.length);
	let acc = 0;
	for (let i = 0; i < widths.length; i++) {
		acc += widths[i];
		prefix[i] = acc;
	}
	const sumW = (k) => (k > 0 ? prefix[k - 1] : 0);
	const noMore = (k) => sumW(k) + (k > 0 ? gapX * (k - 1) : 0);
	const withPlus = (k, pW) => noMore(k) + (k > 0 ? gapX : 0) + pW;

	let kNo = 0;
	for (let i = 1; i <= n; i++) {
		if (noMore(i) <= hostContentW) kNo = i;
		else break;
	}
	if (kNo >= n) return { visible: n, hidden: 0, scroll: false };

	const Wplus = Math.max(hostContentW - (Number.isFinite(safetyPx) ? safetyPx : 0), 0);
	let k = Math.max(kNo, 1);
	while (k > 1) {
		if (withPlus(k, plusWidth) <= Wplus) break;
		k -= 1;
	}

	const hidden = n - k;
	if (hidden === 1 && noMore(n) <= hostContentW) {
		return { visible: n, hidden: 0, scroll: false };
	}
	return { visible: k, hidden, scroll: false };
};
